import { INetworkResult } from '@/coreDomainsModule/networkResult';
import {
  GetCustomerForecastSortTypeEnum,
  GetCustomerForecastResponseAllOfResult,
  CustomerForecastApi,
  GetCustomerForecastResponse,
  ErrorResponse,
  CustomerForecastFilterItem,
  GetCustomerForecastDiffLastMonthResponseAllOfResult,
  GetCustomerForecastDiffLastMonthSortTypeEnum,
  GetCustomerForecastTimelineResponseAllOfResult,
  CustomerForecastTimelineFilterItem,
  CustomerForecastTimelineRequiredFilterItem,
  GetFilterItemsForCustomerForecastTimelineResponseAllOfResult,
  GetLongTermCustomerForecastResponseAllOfResult,
  GetCustomerForecastRateResponseAllOfResult,
  GetCustomerForecastRateSortTypeEnum,
  ExportCustomerForecastRateResponseAllOfResult,
  ExportCustomerForecastRateRequestLanguageEnum,
  ExportCustomerForecastRequestLanguageEnum,
} from '../axios/generated/customerForecastManage';
import { ICustomerForecastRepository } from '../interfaces/ICustomerForecastRepository';
import { customAxiosInstance } from '../axios/axiosInstance';
import { AxiosResponse } from 'axios';
import { IError } from '../interfaces/IError';
import { axiosErrorHandler } from '../axios/axiosError';
import { IFilterInfoResponse } from '../interfaces/IFilterInfoRepository';

/**
 * 客先内示APIの実装クラス
 */
export class CustomerForecastRepository implements ICustomerForecastRepository {
  /**
   *
   * 客先内示情報テーブルから情報の取得を行う
   * @param sortType - ソートの種類（asc/desc）
   * @param limit - 情報の表示最大数
   * @param offset - 表示する情報のオフセット
   * @param receiveYear - 客先内示受領年
   * @param receiveMonth - 客先内示受領月
   * @param filterItem - 検索条件
   * @param unassignedResponsibleUserFlag - 客先内示固有 担当者未割り当て（検索用）フラグ
   * @param sortTarget - どこの列をソート対象にするのかを指定するパラメータ
   * @param sortTargetYear - ソート対象の年(内示値用)
   * @param sortTargetMonth - ソート対象の月(内示値用)
   */
  async getAll(
    sortType: GetCustomerForecastSortTypeEnum,
    limit: number,
    offset: number,
    receiveYear: number,
    receiveMonth: number,
    filterItem: CustomerForecastFilterItem = {},
    unassignedResponsibleUserFlag?: boolean,
    sortTarget?: string,
    sortTargetYear?: number,
    sortTargetMonth?: number
  ): Promise<INetworkResult<GetCustomerForecastResponseAllOfResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const customerForecastApi = new CustomerForecastApi(undefined, undefined, axiosInstance);
    // APIを叩く
    let apiResponse: AxiosResponse<GetCustomerForecastResponse, ErrorResponse>;

    try {
      apiResponse = await customerForecastApi.getCustomerForecast(
        sortType,
        limit,
        offset,
        receiveYear,
        receiveMonth,
        filterItem,
        unassignedResponsibleUserFlag ?? false,
        sortTarget,
        sortTargetYear,
        sortTargetMonth
      );
      switch (apiResponse.status) {
        case 200:
          const result: GetCustomerForecastResponseAllOfResult = apiResponse.data.result;
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 客先内示情報テーブルから内示前月差異情報の取得を行う
   * @param sortType - ソートの種類（asc/desc）
   * @param limit - 情報の表示最大数
   * @param offset - 表示する情報のオフセット
   * @param receiveYear - 客先内示受領年
   * @param receiveMonth - 客先内示受領月
   * @param filterItem - 検索条件
   * @param unassignedResponsibleUserFlag - 客先内示固有 担当者未割り当て（検索用）フラグ
   * @param sortTarget - どこの列をソート対象にするのかを指定するパラメータ
   * @param sortTargetYear - ソート対象年
   * @param sortTargetMonth - ソート対象月
   */
  async getDiffLastMonth(
    sortType: GetCustomerForecastDiffLastMonthSortTypeEnum,
    limit: number,
    offset: number,
    receiveYear: number,
    receiveMonth: number,
    filterItem?: CustomerForecastFilterItem,
    unassignedResponsibleUserFlag?: boolean,
    sortTarget?: string,
    sortTargetYear?: number,
    sortTargetMonth?: number
  ): Promise<INetworkResult<GetCustomerForecastDiffLastMonthResponseAllOfResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const customerForecastApi = new CustomerForecastApi(undefined, undefined, axiosInstance);

    // APIを叩く
    try {
      const apiResponse = await customerForecastApi.getCustomerForecastDiffLastMonth(
        sortType,
        limit,
        offset,
        receiveYear,
        receiveMonth,
        filterItem,
        unassignedResponsibleUserFlag ?? false,
        sortTarget,
        sortTargetYear,
        sortTargetMonth
      );
      switch (apiResponse.status) {
        case 200:
          const result: GetCustomerForecastDiffLastMonthResponseAllOfResult = apiResponse.data.result;
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 客先内示情報テーブルから内示遍歴情報の取得を行う
   * @param baseBusinessId - 関連取引ID
   * @param startYear - 開始年
   * @param startMonth - 開始月
   * @param rangeMonth - 取得する月数
   */
  async getTimeline(
    baseBusinessId: string,
    startYear: number,
    startMonth: number,
    rangeMonth: number
  ): Promise<INetworkResult<GetCustomerForecastTimelineResponseAllOfResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const customerForecastApi = new CustomerForecastApi(undefined, undefined, axiosInstance);

    // APIを叩く
    try {
      const apiResponse = await customerForecastApi.getCustomerForecastTimeline(baseBusinessId, startYear, startMonth, rangeMonth);
      switch (apiResponse.status) {
        case 200:
          const result: GetCustomerForecastTimelineResponseAllOfResult = apiResponse.data.result;
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * フィルター情報を取得
   * 担当者リスト
   * @param filterProperties - フィルター情報
   * @param keyword - 検索キーワード
   */
  async getStaffNameFilterItems(
    filterProperties?: CustomerForecastTimelineFilterItem,
    keyword?: string
  ): Promise<INetworkResult<IFilterInfoResponse, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const customerForecastApi = new CustomerForecastApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse = await customerForecastApi.bulkGetUserNameForCustomerForecastTimeline(filterProperties);
      switch (apiResponse.status) {
        case 200:
          const result = apiResponse.data.result.data;
          return {
            status: {
              type: 'success',
              data: {
                data: result.baseBusinessResponsibleUsers,
                pagingOptions: {
                  pageSize: result.baseBusinessResponsibleUsers.length,
                  offset: result.baseBusinessResponsibleUsers.length,
                  returnCount: result.baseBusinessResponsibleUsers.length,
                },
              },
            },
          };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * フィルター情報を取得
   * 置場CDリスト
   * @param filterProperties - フィルター情報
   * @param keyword - 検索キーワード
   */
  async getWarehouseCodeFilterItems(
    filterProperties?: CustomerForecastTimelineFilterItem,
    keyword?: string
  ): Promise<INetworkResult<IFilterInfoResponse, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const customerForecastApi = new CustomerForecastApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse = await customerForecastApi.bulkGetWarehouseNumberForCustomerForecastTimeline(filterProperties);
      switch (apiResponse.status) {
        case 200:
          const result = apiResponse.data.result.data;
          return {
            status: {
              type: 'success',
              data: {
                data: result.warehouseNumberList,
                pagingOptions: {
                  pageSize: result.warehouseNumberList.length,
                  offset: result.warehouseNumberList.length,
                  returnCount: result.warehouseNumberList.length,
                },
              },
            },
          };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * フィルター情報を取得
   * 置場名リスト
   * @param filterProperties - フィルター情報
   * @param keyword - 検索キーワード
   */
  async getWarehouseFilterItems(
    filterProperties?: CustomerForecastTimelineFilterItem,
    keyword?: string
  ): Promise<INetworkResult<IFilterInfoResponse, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const customerForecastApi = new CustomerForecastApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse = await customerForecastApi.bulkGetWarehouseForCustomerForecastTimeline(filterProperties);
      switch (apiResponse.status) {
        case 200:
          const result = apiResponse.data.result.data;
          return {
            status: {
              type: 'success',
              data: {
                data: result.warehouseList,
                pagingOptions: {
                  pageSize: result.warehouseList.length,
                  offset: result.warehouseList.length,
                  returnCount: result.warehouseList.length,
                },
              },
            },
          };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * フィルター情報を取得
   * 仕入先CDリスト
   * @param filterProperties - フィルター情報
   * @param keyword - 検索キーワード
   */
  async getSupplierCodeFilterItems(
    filterProperties?: CustomerForecastTimelineFilterItem,
    keyword?: string
  ): Promise<INetworkResult<IFilterInfoResponse, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const customerForecastApi = new CustomerForecastApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse = await customerForecastApi.bulkGetSupplierNumberForCustomerForecastTimeline(filterProperties);
      switch (apiResponse.status) {
        case 200:
          const result = apiResponse.data.result.data;
          return {
            status: {
              type: 'success',
              data: {
                data: result.supplierNumberList,
                pagingOptions: {
                  pageSize: result.supplierNumberList.length,
                  offset: result.supplierNumberList.length,
                  returnCount: result.supplierNumberList.length,
                },
              },
            },
          };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * フィルター情報を取得
   * 仕入先名リスト
   * @param filterProperties - フィルター情報
   * @param keyword - 検索キーワード
   */
  async getSupplierNameFilterItems(
    filterProperties?: CustomerForecastTimelineFilterItem,
    keyword?: string
  ): Promise<INetworkResult<IFilterInfoResponse, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const customerForecastApi = new CustomerForecastApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse = await customerForecastApi.bulkGetSupplierNameForCustomerForecastTimeline(filterProperties);
      switch (apiResponse.status) {
        case 200:
          const result = apiResponse.data.result.data;
          return {
            status: {
              type: 'success',
              data: {
                data: result.supplierNameList,
                pagingOptions: {
                  pageSize: result.supplierNameList.length,
                  offset: result.supplierNameList.length,
                  returnCount: result.supplierNameList.length,
                },
              },
            },
          };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * フィルター情報を取得
   * 商品CDリスト
   * @param filterProperties - フィルター情報
   * @param keyword - 検索キーワード
   */
  async getBusinessCodeFilterItems(
    filterProperties?: CustomerForecastTimelineFilterItem,
    keyword?: string
  ): Promise<INetworkResult<IFilterInfoResponse, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const customerForecastApi = new CustomerForecastApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse = await customerForecastApi.bulkGetBusinessNumberForCustomerForecastTimeline(filterProperties);
      switch (apiResponse.status) {
        case 200:
          const result = apiResponse.data.result.data;
          return {
            status: {
              type: 'success',
              data: {
                data: result.businessNumberList,
                pagingOptions: {
                  pageSize: result.businessNumberList.length,
                  offset: result.businessNumberList.length,
                  returnCount: result.businessNumberList.length,
                },
              },
            },
          };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * フィルター情報を取得
   * 商品名リスト
   * @param filterProperties - フィルター情報
   * @param keyword - 検索キーワード
   */
  async getBusinessNameFilterItems(
    filterProperties?: CustomerForecastTimelineFilterItem,
    keyword?: string
  ): Promise<INetworkResult<IFilterInfoResponse, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const customerForecastApi = new CustomerForecastApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse = await customerForecastApi.bulkGetBusinessNameForCustomerForecastTimeline(filterProperties);
      switch (apiResponse.status) {
        case 200:
          const result = apiResponse.data.result.data;
          return {
            status: {
              type: 'success',
              data: {
                data: result.businessNameList,
                pagingOptions: {
                  pageSize: result.businessNameList.length,
                  offset: result.businessNameList.length,
                  returnCount: result.businessNameList.length,
                },
              },
            },
          };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * フィルター情報を取得
   * 容量リスト
   * @param filterProperties - フィルター情報
   * @param keyword - 検索キーワード
   */
  async getPackageAmountFilterItems(
    filterProperties?: CustomerForecastTimelineFilterItem,
    keyword?: string
  ): Promise<INetworkResult<IFilterInfoResponse, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const customerForecastApi = new CustomerForecastApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse = await customerForecastApi.bulkGetPackageForCustomerForecastTimeline(filterProperties);
      switch (apiResponse.status) {
        case 200:
          const result = apiResponse.data.result.data;
          return {
            status: {
              type: 'success',
              data: {
                data: result.packageList,
                pagingOptions: {
                  pageSize: result.packageList.length,
                  offset: result.packageList.length,
                  returnCount: result.packageList.length,
                },
              },
            },
          };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * フィルター情報を取得
   * 売上先CDリスト
   * @param filterProperties - フィルター情報
   * @param keyword - 検索キーワード
   */
  async getCustomerCodeFilterItems(
    filterProperties?: CustomerForecastTimelineFilterItem,
    keyword?: string
  ): Promise<INetworkResult<IFilterInfoResponse, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const customerForecastApi = new CustomerForecastApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse = await customerForecastApi.bulkGetCustomerNumberForCustomerForecastTimeline(filterProperties);
      switch (apiResponse.status) {
        case 200:
          const result = apiResponse.data.result.data;
          return {
            status: {
              type: 'success',
              data: {
                data: result.customerNumberList,
                pagingOptions: {
                  pageSize: result.customerNumberList.length,
                  offset: result.customerNumberList.length,
                  returnCount: result.customerNumberList.length,
                },
              },
            },
          };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * フィルター情報を取得
   * 売上先名リスト
   * @param filterProperties - フィルター情報
   * @param keyword - 検索キーワード
   */
  async getCustomerNameFilterItems(
    filterProperties?: CustomerForecastTimelineFilterItem,
    keyword?: string
  ): Promise<INetworkResult<IFilterInfoResponse, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const customerForecastApi = new CustomerForecastApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse = await customerForecastApi.bulkGetCustomerNameForCustomerForecastTimeline(filterProperties);
      switch (apiResponse.status) {
        case 200:
          const result = apiResponse.data.result.data;
          return {
            status: {
              type: 'success',
              data: {
                data: result.customerNameList,
                pagingOptions: {
                  pageSize: result.customerNameList.length,
                  offset: result.customerNameList.length,
                  returnCount: result.customerNameList.length,
                },
              },
            },
          };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * フィルター情報を取得
   * 送り先CDリスト
   * @param filterProperties - フィルター情報
   * @param keyword - 検索キーワード
   */
  async getSalesDestinationCodeFilterItems(
    filterProperties?: CustomerForecastTimelineFilterItem,
    keyword?: string
  ): Promise<INetworkResult<IFilterInfoResponse, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const customerForecastApi = new CustomerForecastApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse = await customerForecastApi.bulkGetSalesDestinationNumberForCustomerForecastTimeline(filterProperties);
      switch (apiResponse.status) {
        case 200:
          const result = apiResponse.data.result.data;
          return {
            status: {
              type: 'success',
              data: {
                data: result.salesDestinationNumberList,
                pagingOptions: {
                  pageSize: result.salesDestinationNumberList.length,
                  offset: result.salesDestinationNumberList.length,
                  returnCount: result.salesDestinationNumberList.length,
                },
              },
            },
          };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * フィルター情報を取得
   * 送り先名リスト
   * @param filterProperties - フィルター情報
   * @param keyword - 検索キーワード
   */
  async getSalesDestinationNameFilterItems(
    filterProperties?: CustomerForecastTimelineFilterItem,
    keyword?: string
  ): Promise<INetworkResult<IFilterInfoResponse, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const customerForecastApi = new CustomerForecastApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse = await customerForecastApi.bulkGetSalesDestinationNameForCustomerForecastTimeline(filterProperties);
      switch (apiResponse.status) {
        case 200:
          const result = apiResponse.data.result.data;
          return {
            status: {
              type: 'success',
              data: {
                data: result.salesDestinationNameList,
                pagingOptions: {
                  pageSize: result.salesDestinationNameList.length,
                  offset: result.salesDestinationNameList.length,
                  returnCount: result.salesDestinationNameList.length,
                },
              },
            },
          };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 関連取引IDを取得する
   * @param filterItems - フィルター情報
   */
  async getBaseBusinessId(filterItems: CustomerForecastTimelineRequiredFilterItem): Promise<INetworkResult<string, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const customerForecastApi = new CustomerForecastApi(undefined, undefined, axiosInstance);
    try {
      const apiResponse = await customerForecastApi.getBaseBusinessIdForCustomerForecastTimeline(filterItems);
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: apiResponse.data.result.data.baseBusinessId } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * フィルター情報を取得
   * @param baseBusinessId - 関連取引ID
   */
  async getFilterItems(
    baseBusinessId: string
  ): Promise<INetworkResult<GetFilterItemsForCustomerForecastTimelineResponseAllOfResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const customerForecastApi = new CustomerForecastApi(undefined, undefined, axiosInstance);

    // APIを叩く
    try {
      const apiResponse = await customerForecastApi.getFilterItemsForCustomerForecastTimeline(baseBusinessId);
      switch (apiResponse.status) {
        case 200:
          const result: GetFilterItemsForCustomerForecastTimelineResponseAllOfResult = apiResponse.data.result;
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 指定した期間の客先内示を取得する
   * @param targetStartYear - 対象開始年
   * @param targetStartMonth - 対象開始月
   * @param targetEndYear - 対象終了年
   * @param targetEndMonth - 対象終了月
   * @param limit - 取得件数
   * @param offset - 取得開始位置
   * @param filterItem - フィルター対象の項目と情報 (aggregateBusinessIdを含むJSON文字列)
   */
  async getLongterm(
    targetStartYear: number,
    targetStartMonth: number,
    targetEndYear: number,
    targetEndMonth: number,
    limit: number,
    offset: number,
    filterItem?: CustomerForecastFilterItem
  ): Promise<INetworkResult<GetLongTermCustomerForecastResponseAllOfResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    if (!axiosInstance) {
      const error: IError = {
        code: 'unknown',
        message: 'Axios instance could not be created.',
        statusCode: 0,
      };
      return { status: { type: 'error', error: error } };
    }
    const customerForecastApi = new CustomerForecastApi(undefined, undefined, axiosInstance);
    // APIを叩く
    try {
      const apiResponse = await customerForecastApi.getLongTermCustomerForecast(
        targetStartYear,
        targetStartMonth,
        targetEndYear,
        targetEndMonth,
        limit,
        offset,
        filterItem ? JSON.stringify(filterItem) : undefined
      );
      switch (apiResponse.status) {
        case 200:
          const result: GetLongTermCustomerForecastResponseAllOfResult = apiResponse.data.result;
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 内示正答率情報を取得する
   * @param sortType - ソートの種類（asc/desc）
   * @param receiveYear - 受領年
   * @param receiveMonth - 受領月
   * @param displayMonths - 表示月数
   * @param limit - 情報の表示最大数
   * @param offset - 表示する情報のオフセット
   * @param forecastForReceiptResponsibleUserId - 受領用内示担当者ID（オプション）
   * @param sortTarget - どこの列をソート対象にするのかを指定するパラメータ
   * @param sortTargetYear - ソート対象の年
   * @param sortTargetMonth - ソート対象の月
   */
  async getForecastRate(
    sortType: GetCustomerForecastRateSortTypeEnum,
    receiveYear: number,
    receiveMonth: number,
    displayMonths: number,
    limit: number,
    offset: number,
    forecastForReceiptResponsibleUserId?: string,
    sortTarget?: string,
    sortTargetYear?: number,
    sortTargetMonth?: number
  ): Promise<INetworkResult<GetCustomerForecastRateResponseAllOfResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const customerForecastApi = new CustomerForecastApi(undefined, undefined, axiosInstance);

    // APIを叩く
    try {
      const apiResponse = await customerForecastApi.getCustomerForecastRate(
        sortType,
        limit,
        offset,
        receiveYear,
        receiveMonth,
        displayMonths,
        forecastForReceiptResponsibleUserId,
        sortTarget,
        sortTargetYear,
        sortTargetMonth
      );

      switch (apiResponse.status) {
        case 200:
          const result: GetCustomerForecastRateResponseAllOfResult = apiResponse.data.result;
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 内示正答率情報をエクスポートする
   * @param forecastForReceiptId - 受領用内示ID
   * @param startYear - 開始年
   * @param startMonth - 開始月
   * @param endYear - 終了年
   * @param endMonth - 終了月
   * @param receiveYear - 内示受領年
   * @param receiveMonth - 内示受領月
   * @param language - 出力言語（ja: 日本語、en: 英語）
   */
  async exportForecastRate(
    forecastForReceiptId: string,
    startYear: number,
    startMonth: number,
    endYear: number,
    endMonth: number,
    receiveYear: number,
    receiveMonth: number,
    language: string
  ): Promise<INetworkResult<ExportCustomerForecastRateResponseAllOfResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const customerForecastApi = new CustomerForecastApi(undefined, undefined, axiosInstance);

    // 言語パラメータの型チェック
    const validLanguage: ExportCustomerForecastRateRequestLanguageEnum =
      language === ExportCustomerForecastRateRequestLanguageEnum.En
        ? ExportCustomerForecastRateRequestLanguageEnum.En
        : ExportCustomerForecastRateRequestLanguageEnum.Ja;

    // APIを叩く
    try {
      const apiResponse = await customerForecastApi.exportCustomerForecastRate({
        forecastForReceiptId,
        startYear,
        startMonth,
        endYear,
        endMonth,
        receiveYear,
        receiveMonth,
        language: validLanguage,
      });

      switch (apiResponse.status) {
        case 200:
          const result: ExportCustomerForecastRateResponseAllOfResult = apiResponse.data.result;
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 客先内示をエクスポートする
   * @param startYear - 開始年
   * @param startMonth - 開始月
   * @param endYear - 終了年
   * @param endMonth - 終了月
   * @param receivedYear - 受領年
   * @param receivedMonth - 受領月
   * @param language - 出力言語（ja: 日本語、en: 英語）
   * @param filterItem - フィルター項目
   */
  async exportCustomerForecast(
    startYear: number,
    startMonth: number,
    endYear: number,
    endMonth: number,
    receivedYear: number,
    receivedMonth: number,
    language: string,
    filterItem?: CustomerForecastFilterItem
  ): Promise<INetworkResult<ExportCustomerForecastRateResponseAllOfResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const customerForecastApi = new CustomerForecastApi(undefined, undefined, axiosInstance);

    // APIを叩く
    try {
      const apiResponse = await customerForecastApi.exportCustomerForecast({
        startYear,
        startMonth,
        endYear,
        endMonth,
        receivedYear,
        receivedMonth,
        language: language as ExportCustomerForecastRequestLanguageEnum,
        filterItem,
      });

      switch (apiResponse.status) {
        case 200:
          const result: ExportCustomerForecastRateResponseAllOfResult = apiResponse.data.result;
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }
}

/**
 * 客先内示のサンプルデータ作成
 */
const createRequestSample = (): {
  sortType: GetCustomerForecastSortTypeEnum;
  limit: number;
  offset: number;
  receiveYear: number;
  receiveMonth: number;
  filterItem?: CustomerForecastFilterItem | undefined;
  unassignedResponsibleUserFlag?: boolean | undefined;
  sortTarget?: string | undefined;
} => {
  return {
    sortType: 'ASC',
    limit: 20,
    offset: 0,
    receiveYear: 2022,
    receiveMonth: 1,
    unassignedResponsibleUserFlag: false,
    sortTarget: 'baseBusinessId',
  };
};

/**
 * API確認用の関数
 * @param customerForecastRepository - リポジトリのインスタンス
 */
export const checkGetCustomerForecast = async (customerForecastRepository: CustomerForecastRepository) => {
  const requestSample = createRequestSample();
  const res = await customerForecastRepository.getAll(
    requestSample.sortType,
    requestSample.limit,
    requestSample.offset,
    requestSample.receiveYear,
    requestSample.receiveMonth,
    requestSample.filterItem,
    requestSample.unassignedResponsibleUserFlag,
    requestSample.sortTarget
  );
  console.log({ レスポンスチェック: res });
};
